import React, { useState, useEffect } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertTriangle,
  Info,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import Button from "./Button";
import CustomSelect from "./CustomSelect";
import {
  getCountriesForPayment,
  getPaymentMethodsForCountry,
  getPaymentMethodConfig,
  validatePaymentField,
} from "../../utils/paymentMethods";

const PaymentRequestModal = ({
  isOpen,
  onClose,
  onSubmit,
  amount = 0,
  currency = "USD",
  affiliateName = "Affiliate",
}) => {
  // Add custom scrollbar and flip animation styles
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 10px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #B8460E 0%, #8B3A0E 50%, #B8460E 100%);
        border-radius: 10px;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 
                    0 1px 2px rgba(0, 0, 0, 0.3);
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(135deg, #D4541A 0%, #A0421A 50%, #D4541A 100%);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 
                    0 1px 3px rgba(0, 0, 0, 0.4);
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb:active {
        background: linear-gradient(135deg, #8B3A0E 0%, #6B2C0A 50%, #8B3A0E 100%);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 
                    0 1px 1px rgba(0, 0, 0, 0.5);
      }
      
      /* Firefox scrollbar */
      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: #B8460E rgba(0, 0, 0, 0.3);
      }

      /* Flip animation for Other country selection */
      @keyframes flipIn {
        0% {
          transform: perspective(400px) rotateY(-90deg);
          opacity: 0;
        }
        40% {
          transform: perspective(400px) rotateY(-10deg);
        }
        70% {
          transform: perspective(400px) rotateY(5deg);
        }
        100% {
          transform: perspective(400px) rotateY(0deg);
          opacity: 1;
        }
      }

      .animate-flip-in {
        animation: flipIn 0.6s ease-out forwards;
        transform-origin: center;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [customCountryName, setCustomCountryName] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [paymentData, setPaymentData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset modal state when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setSelectedCountry("");
      setCustomCountryName("");
      setSelectedPaymentMethod("");
      setPaymentData({});
      setFieldErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const countries = getCountriesForPayment();
  const paymentMethods = selectedCountry
    ? getPaymentMethodsForCountry(selectedCountry)
    : [];
  const currentMethodConfig =
    selectedCountry && selectedPaymentMethod
      ? getPaymentMethodConfig(selectedCountry, selectedPaymentMethod)
      : null;

  const steps = [
    {
      number: 1,
      title: "Select Country",
      description: "Where is your bank account located?",
    },
    {
      number: 2,
      title: "Payment Method",
      description: "How would you like to receive payment?",
    },
    {
      number: 3,
      title: "Account Details",
      description: "Enter your payment information",
    },
    {
      number: 4,
      title: "Review & Submit",
      description: "Confirm your payout request",
    },
  ];

  const handleCountrySelect = (countryCode) => {
    setSelectedCountry(countryCode);
    setSelectedPaymentMethod(""); // Reset payment method when country changes
    setPaymentData({});
    setFieldErrors({});

    // Reset custom country name if switching away from OTHER
    if (countryCode !== "OTHER") {
      setCustomCountryName("");
    }
  };

  const handlePaymentMethodSelect = (methodKey) => {
    setSelectedPaymentMethod(methodKey);
    setPaymentData({});
    setFieldErrors({});
  };

  const handleFieldChange = (fieldName, value) => {
    setPaymentData((prev) => ({ ...prev, [fieldName]: value }));

    // Real-time validation
    const validation = validatePaymentField(
      selectedCountry,
      selectedPaymentMethod,
      fieldName,
      value
    );

    setFieldErrors((prev) => ({
      ...prev,
      [fieldName]: validation.isValid ? null : validation.error,
    }));
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (selectedCountry === "") return false;
        // If OTHER is selected, require custom country name
        if (selectedCountry === "OTHER" && customCountryName.trim() === "")
          return false;
        return true;
      case 2:
        return selectedPaymentMethod !== "";
      case 3:
        if (!currentMethodConfig?.fields) return true;

        const requiredFields = currentMethodConfig.fields.filter(
          (field) => field.required
        );
        const hasAllRequiredFields = requiredFields.every(
          (field) =>
            paymentData[field.name] && paymentData[field.name].trim() !== ""
        );

        const hasValidationErrors = Object.values(fieldErrors).some(
          (error) => error !== null
        );

        return hasAllRequiredFields && !hasValidationErrors;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    } else {
      toast.error("Please complete all required fields");
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      toast.error("Please review your information");
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData = {
        amount,
        currency,
        country: selectedCountry,
        customCountryName:
          selectedCountry === "OTHER" ? customCountryName : undefined,
        paymentMethod: selectedPaymentMethod,
        paymentData,
        affiliateName,
        timestamp: new Date().toISOString(),
      };

      await onSubmit(requestData);
      toast.success("Payout request submitted successfully!");
      onClose();
    } catch (error) {
      console.error("Error submitting payout request:", error);
      toast.error("Failed to submit payout request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-4 mt-2">
      {steps.map((step, index) => (
        <React.Fragment key={step.number}>
          <div className="flex flex-col items-center">
            {/* Step Circle */}
            <div
              className={`
              relative w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold mb-2
              transition-all duration-300 ease-in-out
              ${
                currentStep >= step.number
                  ? "bg-[#B8460E] text-white shadow-lg shadow-[#B8460E]/30"
                  : currentStep === step.number - 1
                    ? "bg-white/20 text-white border border-[#B8460E]/50"
                    : "bg-white/10 text-gray-400 border border-white/20"
              }
            `}
            >
              {currentStep > step.number ? (
                <Check className="w-3 h-3" />
              ) : (
                step.number
              )}

              {/* Active step pulse effect */}
              {currentStep === step.number && (
                <div className="absolute inset-0 rounded-full bg-[#B8460E] animate-ping opacity-75"></div>
              )}
            </div>

            {/* Step Title */}
            <div className="text-center min-w-[90px]">
              <p
                className={`text-xs font-medium ${
                  currentStep >= step.number ? "text-white" : "text-gray-400"
                }`}
              >
                {step.title}
              </p>
            </div>
          </div>

          {/* Connection Line */}
          {index < steps.length - 1 && (
            <div className="flex-1 h-0.5 mx-3 mb-4">
              <div
                className={`
                h-full transition-all duration-300
                ${currentStep > step.number ? "bg-[#B8460E]" : "bg-white/20"}
              `}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderCountrySelection = () => (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">
          Select Your Country
        </h3>
        <p className="text-gray-400">
          Where is your bank account or payment method located?
        </p>
      </div>

      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5">
        <div className="max-h-72 overflow-y-auto custom-scrollbar">
          <div className="space-y-3">
            {countries.map((country) => {
              const isOther = country.value === "OTHER";
              const isOtherSelected = selectedCountry === "OTHER";

              return (
                <div
                  key={country.value}
                  className={`
                    w-full rounded-xl border transition-all duration-500 ease-in-out
                    ${
                      selectedCountry === country.value
                        ? "bg-[#B8460E]/20 border-[#B8460E] text-white shadow-lg shadow-[#B8460E]/20"
                        : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/15 hover:border-white/30"
                    }
                  `}
                >
                  {isOther && isOtherSelected ? (
                    // Show input field when OTHER is selected
                    <div className="p-3 space-y-3 animate-flip-in">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🌍</span>
                        <div className="flex-1">
                          <p className="font-semibold text-base">
                            Other Country
                          </p>
                          <p className="text-xs opacity-75">Currency: USD</p>
                        </div>
                        <Check className="w-5 h-5 text-[#B8460E]" />
                      </div>
                      <div className="pl-11">
                        <input
                          type="text"
                          value={customCountryName}
                          onChange={(e) => setCustomCountryName(e.target.value)}
                          placeholder="Enter your country name"
                          className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 focus:border-[#B8460E] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E]/20 text-sm"
                          autoFocus
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Please specify which country you're located in
                        </p>
                      </div>
                    </div>
                  ) : (
                    // Show regular button
                    <button
                      onClick={() => handleCountrySelect(country.value)}
                      className="w-full p-3 text-left transition-all duration-200"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{country.flag}</span>
                        <div className="flex-1">
                          <p className="font-semibold text-base">
                            {country.label}
                          </p>
                          <p className="text-xs opacity-75">
                            Currency: {country.currency}
                          </p>
                        </div>
                        {selectedCountry === country.value && !isOther && (
                          <Check className="w-5 h-5 text-[#B8460E]" />
                        )}
                      </div>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPaymentMethodSelection = () => (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">
          Choose Payment Method
        </h3>
        <p className="text-gray-400">
          How would you like to receive your payout?
        </p>
      </div>

      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5">
        <div className="space-y-3">
          {paymentMethods.map((method) => {
            const config = getPaymentMethodConfig(
              selectedCountry,
              method.value
            );
            return (
              <button
                key={method.value}
                onClick={() => handlePaymentMethodSelect(method.value)}
                className={`
                    w-full p-4 rounded-xl border transition-all duration-200 text-left
                    ${
                      selectedPaymentMethod === method.value
                        ? "bg-[#B8460E]/20 border-[#B8460E] text-white shadow-lg shadow-[#B8460E]/20"
                        : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/15 hover:border-white/30"
                    }
                  `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="font-semibold text-base">{method.name}</h4>
                      {method.popular && (
                        <span className="px-2 py-1 bg-[#B8460E]/20 text-[#B8460E] text-xs font-medium rounded-full">
                          Popular
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-xs opacity-75">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {config?.processingTime || "1-3 days"}
                      </div>
                    </div>
                  </div>

                  {selectedPaymentMethod === method.value && (
                    <Check className="w-5 h-5 text-[#B8460E] mt-1" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderAccountDetails = () => {
    const fields = currentMethodConfig?.fields || [];

    // Helper function to determine if field should be full width
    const isFullWidthField = (field) => {
      const fullWidthFields = [
        "account_number",
        "iban",
        "bank_details",
        "payment_details",
        "payment_method_name",
        "alipay_account",
        "upi_id",
        "mobile_number",
      ];
      return (
        field.type === "textarea" ||
        fullWidthFields.some((name) => field.name.includes(name)) ||
        field.name.length > 15
      );
    };

    // Group fields into rows
    const groupFields = (fields) => {
      const groups = [];
      let currentGroup = [];

      fields.forEach((field) => {
        if (isFullWidthField(field)) {
          // If we have items in current group, push it first
          if (currentGroup.length > 0) {
            groups.push(currentGroup);
            currentGroup = [];
          }
          // Add full width field as its own group
          groups.push([field]);
        } else {
          currentGroup.push(field);
          // If we have 2 items in group, finalize it
          if (currentGroup.length === 2) {
            groups.push(currentGroup);
            currentGroup = [];
          }
        }
      });

      // Add remaining fields
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }

      return groups;
    };

    const fieldGroups = groupFields(fields);

    return (
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-white mb-2">Account Details</h3>
          <p className="text-gray-400">
            Enter your {currentMethodConfig?.name} information
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5">
          <div className="space-y-3">
            {fieldGroups.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className={
                  group.length === 1 ? "w-full" : "grid grid-cols-2 gap-4"
                }
              >
                {group.map((field) => (
                  <div key={field.name} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      {field.label}
                      {field.required && (
                        <span className="text-red-400 ml-1">*</span>
                      )}
                    </label>

                    {field.type === "select" ? (
                      <CustomSelect
                        value={paymentData[field.name] || ""}
                        onChange={(selectedValue) =>
                          handleFieldChange(field.name, selectedValue)
                        }
                        options={field.options}
                        placeholder={field.placeholder}
                        className={`
                          bg-white/10 border
                          ${fieldErrors[field.name] ? "border-red-400" : "border-white/20"}
                          text-white placeholder-gray-400
                        `}
                      />
                    ) : field.type === "textarea" ? (
                      <textarea
                        value={paymentData[field.name] || ""}
                        onChange={(e) =>
                          handleFieldChange(field.name, e.target.value)
                        }
                        placeholder={field.placeholder}
                        rows={5}
                        className={`
                          w-full px-3 py-2 rounded-xl bg-white/10 border transition-all resize-none text-sm
                          ${
                            fieldErrors[field.name]
                              ? "border-red-400 focus:border-red-400"
                              : "border-white/20 focus:border-[#B8460E]"
                          }
                          text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E]/20
                        `}
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={paymentData[field.name] || ""}
                        onChange={(e) =>
                          handleFieldChange(field.name, e.target.value)
                        }
                        placeholder={field.placeholder}
                        className={`
                          w-full px-3 py-2 rounded-xl bg-white/10 border transition-all text-sm
                          ${
                            fieldErrors[field.name]
                              ? "border-red-400 focus:border-red-400"
                              : "border-white/20 focus:border-[#B8460E]"
                          }
                          text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E]/20
                        `}
                      />
                    )}

                    {field.helpText && (
                      <div className="flex items-start gap-2 text-xs text-gray-400">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{field.helpText}</span>
                      </div>
                    )}

                    {fieldErrors[field.name] && (
                      <div className="flex items-start gap-2 text-xs text-red-400">
                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{fieldErrors[field.name]}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderReviewAndSubmit = () => {
    const countryInfo = countries.find((c) => c.value === selectedCountry);
    const methodInfo = paymentMethods.find(
      (m) => m.value === selectedPaymentMethod
    );

    const maskSensitiveData = (value, fieldName) => {
      if (!value) return "";

      const sensitiveFields = [
        "accountNumber",
        "routingNumber",
        "iban",
        "ifscCode",
        "upiId",
      ];
      if (
        sensitiveFields.some((field) =>
          fieldName.toLowerCase().includes(field.toLowerCase())
        )
      ) {
        return value.length > 4
          ? "*".repeat(value.length - 4) + value.slice(-4)
          : "*".repeat(value.length);
      }
      return value;
    };

    const displayCountryName =
      selectedCountry === "OTHER" ? customCountryName : countryInfo?.label;

    return (
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-white mb-2">Review & Submit</h3>
          <p className="text-gray-400">
            Please review your payout request details
          </p>
        </div>

        <div className="space-y-4">
          {/* Payout Amount */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4">
            <h4 className="text-base font-semibold text-white mb-3">
              Payout Amount
            </h4>
            <div className="text-3xl font-bold text-[#B8460E]">
              {currency} {amount.toLocaleString()}
            </div>
          </div>

          {/* Payment Details */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4">
            <h4 className="text-base font-semibold text-white mb-3">
              Payment Details
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Country:</span>
                <span className="text-white flex items-center gap-2">
                  <span>{countryInfo?.flag || "🌍"}</span>
                  {displayCountryName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Method:</span>
                <span className="text-white">{methodInfo?.name}</span>
              </div>

              {currentMethodConfig?.fields?.map((field) => {
                const value = paymentData[field.name];
                if (!value) return null;

                return (
                  <div key={field.name} className="flex justify-between">
                    <span className="text-gray-400">{field.label}:</span>
                    <span className="text-white font-mono text-right max-w-[60%] break-words">
                      {field.type === "textarea"
                        ? value.length > 50
                          ? value.substring(0, 50) + "..."
                          : value
                        : maskSensitiveData(value, field.name)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Processing Info */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-200">
                <p className="font-medium mb-1">Processing Information</p>
                <p>
                  Your payout will be processed within{" "}
                  <span className="font-medium">
                    {currentMethodConfig?.processingTime || "1-3 business days"}
                  </span>
                  . You'll receive a confirmation email once the payment is
                  sent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderCountrySelection();
      case 2:
        return renderPaymentMethodSelection();
      case 3:
        return renderAccountDetails();
      case 4:
        return renderReviewAndSubmit();
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[999999] p-4">
      <div className="bg-black/90 backdrop-blur-xl border border-white/20 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h2 className="text-xl font-bold text-white">Request Payout</h2>
            <p className="text-gray-400 text-sm mt-1">
              Step {currentStep} of {steps.length}:{" "}
              {steps[currentStep - 1]?.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(85vh-120px)] custom-scrollbar">
          {renderStepIndicator()}

          <div className="min-h-[200px] flex items-start justify-center">
            {renderStepContent()}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-white/10 bg-white/5">
          <div>
            {currentStep > 1 && (
              <Button
                onClick={handlePrev}
                variant="secondary"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border-white/20 px-4 py-2 text-sm"
              >
                <ChevronLeft className="w-3 h-3" />
                Previous
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              Step {currentStep} of {steps.length}
            </span>

            {currentStep < 4 ? (
              <Button
                onClick={handleNext}
                disabled={!validateCurrentStep()}
                className="flex items-center gap-2 bg-[#B8460E] hover:bg-[#8B3A0E] disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 text-sm"
              >
                Next
                <ChevronRight className="w-3 h-3" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !validateCurrentStep()}
                className="flex items-center gap-2 bg-[#B8460E] hover:bg-[#8B3A0E] disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 text-sm"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Request
                    <Check className="w-3 h-3" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestModal;
